import { ampli } from '@/ampli';

import { logger } from '@/lib/logger';

import { trackRwaSwapInitiated } from '@/components/TransactionModule/utils';
import type { TriggerOrderUiLocation } from '@/components/TriggerOrders/utils';

import {
  ADVANCED_ORDER_TYPE_LABELS,
  type AdvancedOrderType,
  getAdvancedOrderProvider,
  isLimitOrderSupportedChainId,
} from '@/constants/advancedOrder';

import {
  SubmitLimitOrderMutationError,
  type SubmitLimitOrderResult,
  toPreSubmissionError,
} from '@/hooks/useSubmitLimitOrder';

import type { LimitOrderIdentity } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { toAmpliSwapOrderType } from '@/utils/advancedOrderAnalytics';
import { trackError } from '@/utils/errors';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';

import type { TokenInputValues } from '../store';
import { buildTokenEventFields } from './analytics';

type SubmitAdvancedOrderOptions = {
  chainId: number;
  sellToken: TokenResult;
  buyToken: TokenResult;
  sellInput: TokenInputValues;
  buyInput: TokenInputValues;
  orderType: AdvancedOrderType;
  matchaReferrer?: string;
  uiLocation?: TriggerOrderUiLocation;
  validate?: () => void;
  submit: () => Promise<SubmitLimitOrderResult>;
  refreshOpenLimitOrders: (identity: LimitOrderIdentity) => void;
  setRecentTransaction: () => void;
};

type SubmitAdvancedOrderResult =
  | { success: true; submission: SubmitLimitOrderResult; identity: LimitOrderIdentity }
  | { success: false; rejected: boolean; error: Error };

export async function submitAdvancedOrder({
  chainId,
  sellToken,
  buyToken,
  sellInput,
  buyInput,
  orderType,
  matchaReferrer,
  uiLocation,
  validate,
  submit,
  refreshOpenLimitOrders,
  setRecentTransaction,
}: SubmitAdvancedOrderOptions): Promise<SubmitAdvancedOrderResult> {
  const advancedOrderProvider = isLimitOrderSupportedChainId(chainId)
    ? getAdvancedOrderProvider(chainId)
    : undefined;
  const swapOrderType = toAmpliSwapOrderType('advanced', orderType);
  try {
    validate?.();
    const eventFields = {
      ...buildTokenEventFields({ source: 'input', sellToken, buyToken, sellInput, buyInput }),
      swap_order_type: swapOrderType,
      matcha_referrer: matchaReferrer,
      ...(uiLocation ? { ui_location: uiLocation } : {}),
    };
    ampli.swapInitiated({
      ...eventFields,
      limit_order_provider: advancedOrderProvider,
    });

    const submission = await submit();
    ampli.transactionSubmitted({
      ...eventFields,
      limit_order_provider: submission.provider,
      swap_tx_hash: submission.orderIdentifier,
    });

    let identity: LimitOrderIdentity;
    switch (submission.provider) {
      case '1inch':
        identity = { provider: submission.provider, orderHash: submission.orderIdentifier };
        break;
      case 'definitive':
        identity = { provider: submission.provider, orderId: submission.orderIdentifier };
        break;
      default: {
        const exhaustiveProvider: never = submission.provider;
        identity = exhaustiveProvider;
      }
    }

    trackRwaSwapInitiated({ sellToken, buyToken, orderType: swapOrderType });
    refreshOpenLimitOrders(identity);
    setRecentTransaction();
    return { success: true, submission, identity };
  } catch (error: unknown) {
    const mutationError =
      error instanceof SubmitLimitOrderMutationError
        ? error
        : toPreSubmissionError(error, undefined, orderType);
    const rejected = mutationError.failureReason === 'user_rejected';
    const originalCause = mutationError.originalCause;
    const trackableError =
      originalCause instanceof Error
        ? originalCause
        : new Error(`${ADVANCED_ORDER_TYPE_LABELS[orderType]} order submission failed`);
    if (!rejected) {
      logger.error(trackableError);
      trackError(trackableError, { orderType });
    }
    trackTransactionFailed({
      sellToken,
      buyToken,
      failureReason: mutationError.failureReason,
      failureStage: mutationError.failureStage,
      advancedOrderProvider,
      swapOrderType,
      matchaReferrer,
      submissionReason: mutationError.submissionReason,
      revertReason: mutationError.revertReason,
      // An orderIdentifier only means "posted" at submission stage.
      swapTxHash:
        mutationError.failureStage === 'submission' ? mutationError.orderIdentifier : undefined,
      ...(uiLocation ? { uiLocation } : {}),
    });
    return { success: false, rejected, error: trackableError };
  }
}
